<template>
  <div class="electrocardiography-panel">
    <div class="no-data-disclaimer" v-if="!data">
      <div class="label">
        <div class="title">{{ $t("nothing_to_show") }}</div>
        <div class="subtitle">
          {{ $t("select_one_ecg") }}
        </div>
      </div>
    </div>
    <template v-else>
      <div class="top-panel">
        <multi-select-component v-if="$asyncComputed.hdData.success"
          label="leads"
          mode="multiple"
          v-model="selectedLeads"
          :options="leadOptions"
        ></multi-select-component>
        <display-value-component
          v-if="$asyncComputed.hdData.success"
          class="intervals"
          label="QT interval"
          :value="`${this.qt.toFixed(2)} s`"
        ></display-value-component>
        <div v-if="$asyncComputed.hdData.success  && selectedLeads.length"
          v-tooltip="'Measure QT'"
          class="measure-toggle-qt"
          :class="{ active: brushMode === 'qt' }"
          @click="brushMode !== 'qt' ? (brushMode = 'qt') : (brushMode = null)"
        ></div>
        <display-value-component
          v-if="$asyncComputed.hdData.success"
          class="intervals"
          label="QRS interval"
          :value="`${this.qrs.toFixed(2)} s`"
        ></display-value-component>
        <div v-if="$asyncComputed.hdData.success && selectedLeads.length"
          v-tooltip="'Measure QRS'"
          class="measure-toggle-qrs"
          :class="{ active: brushMode === 'qrs' }"
          @click="brushMode !== 'qrs' ? (brushMode = 'qrs') : (brushMode = null)"
        ></div>
        <div  v-if="$asyncComputed.hdData.success"
          v-tooltip="'Save new intervals'"
          class="save-intervals"
          @click="saveIntervals"
        ><div>Save</div></div>
        <!-- <div class="brush-toggle" :class="{active: brushMode==='y'}"  @click="brushMode!=='y'?brushMode='y':brushMode=null">BRUSH Y</div> -->
      </div>
      <div class="ecg-x-panel-container">
        <multi-ecg v-if="$asyncComputed.hdData.success"
          :data="hdData"
          :selectedLeads="selectedLeads"
          :leadOptions="leadOptions"
          :qt="this.qt"
          :qrs="this.qrs"
          :ton="this.ton"
          :style="{ marginLeft: 5 + 'px' , marginRight: 15 + 'px'}"
          :brushMode="brushMode"
          v-model="highlightedLine"
          :forcedMargin="{ top: 0 }"
          @modifyQrs="newQrs"
          @modifyQt="newQt"
        ></multi-ecg>
        <loading-overlay class="hd-loading" v-if="$asyncComputed.hdData.updating"></loading-overlay>
        <error-overlay v-else-if="$asyncComputed.hdData.error"></error-overlay>
      </div>
    </template>
  </div>
</template>

<script>
import MultiSelectComponent from "@/components/MultiSelectComponent";
import DisplayValueComponent from "@/components/DisplayValueComponent";
import InputContainer from '@/components/inputs/InputContainer'
import MultiEcg from "@/components/CaseDashboard/MultiEcg";
import LoadingOverlay from '@/components/commons/LoadingOverlay'
import Api from '@/api'
import ApolloAPI from '@/api/apollo_api'

export default {
  name: "ElectrocardiographyPanel",
  components: { MultiSelectComponent, MultiEcg, InputContainer, DisplayValueComponent, LoadingOverlay},
  props: ["data", "highlightedSubject"],
  data() {
    return {
      selectedLeads: [],
      brushMode: null,
      highlightedLine: null,
      saveStatus: 'pending',
      dataUpdating: false,
      qt: 0.5,
      qrs: 0.5,
      ton: 0,
      hdData: []
    };
  },
  mounted() {
    if (this.leadOptions[0]) this.selectedLeads = [this.leadOptions[0]];
  },
  computed: {
    leadOptions() {
      let leadOptions = this.data
        ? this.data[0].sparkline.headers.filter(
            h => h.includes("L") && /\d/.test(h)
          )
        : [];
      return leadOptions
    }
  },
  asyncComputed: {
    async hData() {
      return Api.getSubjectEcg('5','16','76')
      // return this.data.map(d => {
      //   let response = await Api.getSubjectEcg('5','16','76')
      //   console.log('hData', response)
      //   return {
      //     ...d,
      //     sparkline: {
      //       ...d.sparkline,
      //       //Assumes same headers as de "low def" sparkline
      //       rows: response.map(row =>
      //         d.sparkline.headers.map(h => row[h])
      //       )
      //     }
      //   };
      // })
    },
    hdData() {
      return Promise.all(
        (this.data || []).map(d => {
          return ApolloAPI.getEcg({
              ...this.$store.state.paramsData,
              trial: { id: d.trialId },
              subject: d
          }).then(response => {
              console.log('hData', response)
              return {
                ...d,
                sparkline: {
                  ...d.sparkline,
                  //Assumes same headers as de "low def" sparkline
                  rows: response.map(row =>
                    d.sparkline.headers.map(h => row[h])
                  )
                }
              };
            }).catch(() => {return []});
        })
      )
    }
  },
  watch: {
    leadOptions() {
      console.log('data',this.data)
      if (this.leadOptions[0]) this.selectedLeads = [this.leadOptions[0]];
      if (this.data.length > 0) {
        this.ton = this.data[0].scalars[8].value * 0.001
        this.qrs = this.data[0].scalars[0].value * 0.001
        this.qt = this.data[0].scalars[1].value * 0.001
      }
    },
    highlightedSubject() {
      this.highlightedLine = this.highlightedSubject;
    },
    highlightedLine() {
      if (
        !this.highlightedSubject ||
        !this.highlightedLine ||
        this.highlightedLine.subject.id !== this.highlightedSubject.subject.id
      ) {
        this.$emit("highlight", this.highlightedLine);
      }
    }
  },
  methods: {
    newQt(value) {
      this.qt = value[1]-value[0]
      this.ton = value[0]
    },
    newQrs(value) {
      this.qrs = value[1]-value[0]
      this.ton = value[0]
    },
    saveIntervals() {
      if (this.data.length == 1) {
        let d = this.data[0];
        // Post-processing is done in third cardiac cycle (e.g: Ton is from the 3rd cycle)
        d.scalars[0].value = this.qrs *1000  // new QRS duration
        d.scalars[1].value = this.qt *1000 // new QT duration
        d.scalars[8].value = this.ton *1000 // new Ton
        let scalarsObject = {
          type:"scalars",
          scalars:d.scalars
        }
        console.log(scalarsObject)

        Promise.all([Api.modifySubjectGlobals(this.$store.state.paramsData.project.id, d.trialId, d.subject.id, scalarsObject)])
        .then(
          response => {
            this.saveStatus = 'success'
            alert('New interval values saved successfully')
          }), error => {
            this.saveStatus = 'error'
            alert('There was an error saving the new intervals')
          }
      }
    }
    // onEcgSelect(d){
    //   console.log("selected on ECG",d)
    //   this.$emit('highlight',d)
    //   // HABRIA QUE "subirlo" hasta el TrialREsultsView, pero como se muestra en el scatter?!?
    // }
  }
};
</script>

<style lang="stylus" scoped>
.electrocardiography-panel
  position: relative
  display: flex
  flex-direction: column
  overflow: hidden

  .ecg-x-panel-container
    position: relative
    // flex: 1 1 auto
    flex: 1 1 0%
    overflow: hidden
    margin-bottom: 40px
    background: #fafafa

    .hd-loading
      position: absolute
      top: 0
      left: 0
      width: 100%
      height: 100%
      background: rgba(255, 255, 255, 0.7)
      color: #000

      .text
        position: absolute
        top: 50%
        left: 50%
        transform: translate(-50%, -50%)

  .top-panel
    display: flex
    .measure-toggle-qt
      position: relative
      margin: auto
      margin-left: 0px
      width: 30px
      height: 30px
      border: 1px solid #FCFCFF
      border-radius: 50%
      background: #FCFCFF
      cursor: pointer

      &:after
        position: absolute
        content: ""
        m-icon("measure", 14)
        top: 50%
        left: 50%
        opacity: 0.6
        transform: translate(-50%, -50%)

      &:hover
        border: 1px solid #B6BED1

        &:after
          opacity: 0.5

      &.active
        font-weight: bold

        &:after
          opacity: 1

    .measure-toggle-qrs
      position: relative
      margin: auto
      margin-left: 0px
      width: 30px
      height: 30px
      border: 1px solid #FCFCFF
      border-radius: 50%
      background: #FCFCFF
      cursor: pointer

      &:after
        position: absolute
        content: ""
        m-icon("measure", 14)
        top: 50%
        left: 50%
        opacity: 0.6
        transform: translate(-50%, -50%)

      &:hover
        border: 1px solid #B6BED1

        &:after
          opacity: 0.5

      &.active
        font-weight: bold

        &:after
          opacity: 1
    

    .multi-select-component
      flex: 0 0 auto
      margin: 15px 30px

    .intervals
      position: relative
      margin: auto
      margin-right: 10px

    .save-intervals
      position: relative
      margin: auto
      margin-right: 10px
      color: black
      width: 60px
      height: 30px
      border: 1px solid #FCFCFF
      border-radius: 10%
      background: #FCFCFF
      cursor: pointer
      display: flex
      align-items: center
      justify-content: center
      m-font-size(12, 14)
 

  .no-data-disclaimer
    .label
      position: absolute
      top: 50%
      left: 50%
      color: #fff
      text-align: center
      transform: translate(-50%, -50%)

      .title
        m-font-size(18)
        margin-bottom: 20px
        font-weight: $fw-bold

      .subtitle
        m-font-size(14)
</style>
